import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, LogOut, Search, User } from 'lucide-react';
import { useAuth, useLogout } from '@features/auth';
import { useUIStore, type Theme } from '@store/uiStore';
import { supportedLanguages } from '@config/i18n';
import { GlobalSearch } from '@features/search';
import { useIsMobile } from '@common/hooks/useMediaQuery';
import {
  ActionMenu,
  Avatar,
  Header as AlumaHeader,
  IconButton,
  LanguageSwitcher,
  Modal,
  ThemeToggle,
} from '@ntconsult/aluma-react';

const languageOptions = supportedLanguages.map((lang) => ({
  code: lang.code,
  label: lang.name,
  shortLabel: lang.code.split('-')[0].toUpperCase(),
  flag: <span className="text-base leading-none">{lang.flag}</span>,
}));

export function Header() {
  const { t, i18n } = useTranslation(['files', 'settings', 'auth', 'common']);
  const { user } = useAuth();
  const logout = useLogout();
  const { toggleSidebar, theme, setTheme } = useUIStore();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  return (
    <AlumaHeader>
      <AlumaHeader.Start>
        <IconButton
          variant="ghost"
          onClick={toggleSidebar}
          label={t('common:actions.toggleSidebar', { defaultValue: 'Alternar barra lateral' })}
          icon={<Menu className="w-5 h-5" aria-hidden="true" />}
        />
        <div className="flex items-center gap-2 min-w-0">
          <img src="/icone_logo_nt.svg" alt="NT Files" className="w-5 h-5 shrink-0" />
          <h1 className="text-lg sm:text-xl font-semibold text-foreground truncate">Files</h1>
        </div>
      </AlumaHeader.Start>

      <AlumaHeader.Center>
        <div className="hidden md:flex w-full justify-center">
          <GlobalSearch />
        </div>
      </AlumaHeader.Center>

      <AlumaHeader.End>
        <IconButton
          className="md:hidden"
          variant="ghost"
          onClick={() => setMobileSearchOpen(true)}
          label={t('common:search.label', { defaultValue: 'Busca global' })}
          icon={<Search className="w-5 h-5" aria-hidden="true" />}
        />
        <div className="hidden sm:block">
          <LanguageSwitcher
            languages={languageOptions}
            value={i18n.language}
            onValueChange={(code) => i18n.changeLanguage(code)}
            display="short"
            triggerVariant="ghost"
            triggerLabel={t('settings:selectLanguage', { defaultValue: 'Selecionar idioma' })}
          />
        </div>
        <ThemeToggle
          mode="icon"
          value={theme}
          onValueChange={(value) => setTheme(value as Theme)}
          aria-label={t('settings:toggleTheme', { defaultValue: 'Alternar tema' })}
        />

        <ActionMenu
          align="right"
          trigger={
            <button
              type="button"
              aria-label={user?.name ?? 'User menu'}
              className="flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors hover:bg-muted disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2"
            >
              <Avatar name={user?.name || 'U'} size="sm" />
              <span className="text-sm font-medium text-foreground hidden sm:block">
                {user?.name}
              </span>
            </button>
          }
        >
          <div className="px-3 py-2 border-b border-border">
            <p className="text-sm font-medium text-foreground">{user?.name}</p>
            <p className="text-xs text-foreground-muted">{user?.email}</p>
          </div>
          <ActionMenu.Item icon={<User className="w-4 h-4" aria-hidden="true" />} onClick={() => navigate('/audit')}>
            {t('settings:myActivity')}
          </ActionMenu.Item>
          <ActionMenu.Separator />
          <ActionMenu.Item
            variant="danger"
            icon={<LogOut className="w-4 h-4" aria-hidden="true" />}
            onClick={logout}
          >
            {t('auth:logout')}
          </ActionMenu.Item>
        </ActionMenu>
      </AlumaHeader.End>

      {isMobile && (
        <Modal
          open={mobileSearchOpen}
          onClose={() => setMobileSearchOpen(false)}
          ariaLabel={t('common:search.label', { defaultValue: 'Busca global' })}
          size="lg"
          className="sm:max-w-lg h-[calc(100dvh-2rem)] sm:h-auto"
        >
          <Modal.Body className="flex flex-col min-h-0 p-0">
            <GlobalSearch
              inline
              autoFocus
              onNavigate={() => setMobileSearchOpen(false)}
            />
          </Modal.Body>
        </Modal>
      )}
    </AlumaHeader>
  );
}
